// Parking Journey: destination → ranked options → persistent journey.
// Everything is device-local (localStorage), anonymous, and survives a trip to
// Google Maps / Waze and back.

import { distanceMeters } from "./carLocation";
import { api } from "./api";

const KEY = "spotdrop_journey_v1";
const PENALTY_KEY = "spotdrop_place_penalties_v1";

export const PENALTY_MS = 20 * 60 * 1000; // a failed attempt de-ranks for 20 min
const MAX_JOURNEY_AGE_MS = 6 * 60 * 60 * 1000;

const TYPE_LABEL = {
  live: "Live SpotDrop",
  free_parking: "Free parking",
  paid_parking: "Paid parking",
  car_park: "Public car park",
  motorcycle_parking: "Motorcycle parking",
  street_parking: "Street parking",
};

const TIER = { live: 0, free_parking: 1, paid_parking: 2, car_park: 2, street_parking: 2, motorcycle_parking: 3 };
const MAX_STREET_OPTIONS = 2;

/* Closest point on a LineString to the destination: a street section is only as
   relevant as its nearest parkable metre. */
function anchorOnLine(geometry, dest) {
  const coords = geometry?.type === "LineString" ? geometry.coordinates : null;
  if (!coords || coords.length < 2) return null;
  let best = null;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const [ax, ay] = coords[i];
    const [bx, by] = coords[i + 1];
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let t = len2 ? ((dest.lng - ax) * dx + (dest.lat - ay) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const lng = ax + t * dx;
    const lat = ay + t * dy;
    const d = distanceMeters(dest.lat, dest.lng, lat, lng);
    if (!best || d < best.d) best = { lat, lng, d };
  }
  return best;
}

/* One street keeps one recommendation: neighbouring sections of the same road
   are the same parking opportunity to a driver. */
function limitStreetOptions(options) {
  const byStreet = new Map();
  const rest = [];
  for (const o of options) {
    if (o.kind !== "street_parking") {
      rest.push(o);
      continue;
    }
    const key = o.name || `seg-${Math.round(o.lat * 2000)},${Math.round(o.lng * 2000)}`;
    const prev = byStreet.get(key);
    if (!prev || o.distance_m < prev.distance_m) byStreet.set(key, o);
  }
  const streets = [...byStreet.values()].sort((a, b) => a.distance_m - b.distance_m);
  return [...rest, ...streets.slice(0, MAX_STREET_OPTIONS)];
}

// Destination relevance: a candidate must be genuinely walkable from where the
// driver is actually going, otherwise it is not an option at all.
export const LIVE_MAX_M = 700;
export const PLACE_MAX_M = 1000;
export const MAX_WALK_MIN = 15;
const WALK_M_PER_MIN = 80;

const walkMetres = (o) => (o.walk_m != null ? o.walk_m : o.distance_m);
const walkMinutes = (o) =>
  o.walk_min != null ? o.walk_min : Math.round(walkMetres(o) / WALK_M_PER_MIN);
const validCoords = (o) =>
  Number.isFinite(o?.lat) && Number.isFinite(o?.lng) && (o.lat !== 0 || o.lng !== 0);

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
};

/* ---------- journey state ---------- */

export function loadJourney() {
  const j = readJson(KEY, null);
  if (!j?.destination || !j?.selected || !j?.started_at) return null;
  if (Date.now() - j.started_at > MAX_JOURNEY_AGE_MS) {
    clearJourney();
    return null;
  }
  return j;
}

export function saveJourney(journey) {
  writeJson(KEY, journey);
  return journey;
}

export function clearJourney() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/* ---------- temporary "it was full" penalties ---------- */

export function penalizedIds() {
  const map = readJson(PENALTY_KEY, {});
  const now = Date.now();
  const live = {};
  for (const [id, until] of Object.entries(map)) if (until > now) live[id] = until;
  if (Object.keys(live).length !== Object.keys(map).length) writeJson(PENALTY_KEY, live);
  return new Set(Object.keys(live));
}

/** Local de-rank + a shared, self-expiring report on the backend. */
export function penalizePlace(placeId) {
  if (!placeId) return;
  const map = readJson(PENALTY_KEY, {});
  map[placeId] = Date.now() + PENALTY_MS;
  writeJson(PENALTY_KEY, map);
  api.post(`/parking-places/${placeId}/occupied`).catch(() => {});
}

/* ---------- ranking ---------- */

function statusFor(place, penalized) {
  if (penalized) return "Reported full recently";
  if (place.confidence === "confirmed") return "✓ Verified by SpotDrop";
  if (place.confidence === "reported") return "Community reported — not verified";
  if (place.source === "osm") return "OpenStreetMap data — not verified";
  return "Availability not guaranteed";
}

/** Ranked options for a destination. Walking time dominates: an excellent
 *  5-minute walk always beats a "better" type that is further away. Candidates
 *  outside the destination radius are not options at all. */
export function buildOptions({ destination, places, liveSpots, maxPlaceM = PLACE_MAX_M }) {
  const penalties = penalizedIds();
  const options = [];

  (liveSpots || [])
    .filter((s) => !s.claimed && !s.mine && validCoords(s))
    .forEach((s) => {
      const distance_m = Math.round(distanceMeters(destination.lat, destination.lng, s.lat, s.lng));
      if (distance_m > LIVE_MAX_M) return;
      options.push({
        id: `live-${s.id}`,
        source: "live",
        spot_id: s.id,
        kind: "live",
        lat: s.lat,
        lng: s.lng,
        distance_m,
        type_label: TYPE_LABEL.live,
        status: "Live now",
        tier: TIER.live,
        penalized: false,
      });
    });

  (places || []).forEach((p) => {
    // Unusable parking (no public access, permit only, residents hours active)
    // is never a recommendation, whatever the distance.
    if (p.rule && p.rule.usable === false) return;
    if (!validCoords(p)) return;
    // A street section is anchored on its metre closest to the destination.
    const anchor = p.kind === "street_parking" ? anchorOnLine(p.geometry, destination) : null;
    const lat = anchor ? anchor.lat : p.lat;
    const lng = anchor ? anchor.lng : p.lng;
    const distance_m = Math.round(distanceMeters(destination.lat, destination.lng, lat, lng));
    if (distance_m > maxPlaceM) return;
    const penalized = penalties.has(p.id) || !!p.recently_occupied;
    options.push({
      id: `place-${p.id}`,
      source: "place",
      place_id: p.id,
      kind: p.kind,
      name: p.name || null,
      lat,
      lng,
      geometry: p.geometry || null,
      side: p.side || null,
      distance_m,
      type_label: p.rule?.label || TYPE_LABEL[p.kind] || "Parking",
      accessible: p.accessible || null,
      status: statusFor(p, penalized),
      tier: TIER[p.kind] ?? 3,
      penalized,
    });
  });

  return resortOptions(limitStreetOptions(dedupe(options))).slice(0, 8);
}

/** Same spot mapped twice (OSM + community, or two nodes on one car park). */
function dedupe(options) {
  const seen = new Map();
  for (const o of options) {
    const key = `${o.source}-${o.lat.toFixed(4)},${o.lng.toFixed(4)}`;
    const prev = seen.get(key);
    if (!prev || o.tier < prev.tier) seen.set(key, o);
  }
  return [...seen.values()];
}

/** Ranking: walking minutes → live availability (same-minute tie-break) → real distance. */
export function resortOptions(options) {
  return [...options].sort(
    (a, b) =>
      (a.penalized ? 1 : 0) - (b.penalized ? 1 : 0) ||
      walkMinutes(a) - walkMinutes(b) ||
      a.tier - b.tier ||
      walkMetres(a) - walkMetres(b)
  );
}

/** Final relevance check once real routes are known: never over 15 min walk. */
export function filterWalkable(options) {
  return options.filter((o) => walkMinutes(o) <= MAX_WALK_MIN);
}

export function formatWalk(metresOrOption, maybeMin) {
  if (metresOrOption && typeof metresOrOption === "object") {
    const o = metresOrOption;
    if (o.walk_m != null && o.walk_min != null) return `${o.walk_min} min walk · ${o.walk_m} m`;
    return formatWalk(o.distance_m);
  }
  const metres = metresOrOption;
  if (metres == null) return "";
  if (maybeMin != null) return `${maybeMin} min walk · ${metres} m`;
  return metres < 950 ? `${Math.round(metres / 10) * 10} m away` : `${(metres / 1000).toFixed(1)} km away`;
}

/* ---------- external navigation ---------- */

const NAV_KEY = "spotdrop_nav_app";

export function preferredNavApp() {
  try {
    const v = localStorage.getItem(NAV_KEY);
    return v === "waze" || v === "google" ? v : null;
  } catch {
    return null;
  }
}

export function rememberNavApp(app) {
  try {
    localStorage.setItem(NAV_KEY, app);
  } catch {
    /* ignore */
  }
}

export function buildNavAppUrl(app, lat, lng) {
  if (app === "waze") return `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
}

/* Driving target for a car park: its entrance when we know one (the polygon centre
   can sit inside a building), otherwise the record itself. Walking distances and
   the Assistant ranking keep using the record's own point. */
export function navTarget(place, from) {
  const list = place?.entrances || [];
  if (!list.length) return { lat: place.lat, lng: place.lng, via: "place" };
  const origin = from?.lat != null ? from : place;
  const best = list.reduce((a, b) =>
    distanceMeters(origin.lat, origin.lng, a.lat, a.lng) <=
    distanceMeters(origin.lat, origin.lng, b.lat, b.lng)
      ? a
      : b
  );
  return { lat: best.lat, lng: best.lng, via: "entrance" };
}
